import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { getFlag, setFlag, getCrumbs, spendCrumbs } from '../core/save.js';
import { questsForGiver, acceptQuest, turnIn, isReady, QUESTS, flagObjective } from '../core/quests.js';
import { addIngredient } from '../core/ingredients.js';
import '../ui/space.css';

// Moon Town — a bubble-domed lunar settlement reached by rocket from the
// spaceport. Cheese vendors and dog pirates mill about in low gravity. Buy moon
// cheese fuel + lunar ingredients at Brie Bella's shop, visit the Moon Castle,
// and (once the King grants the nav chip) install it at the landed ship.

const MOON = 0x9A9AA6, MOON_D = 0x76767F, CRATER = 0x64646E, NIGHT = 0x0A0A1E;
const FUEL_COST = 25;
const MOON_INGREDIENTS = [
  { id: 'moon_cheese', name: '🧀 Moon Cheese', price: 6 },
  { id: 'moon_egg', name: '🥚 Moon Egg', price: 6 },
  { id: 'moon_milk', name: '🥛 Moon Milk', price: 6 },
  { id: 'stardust', name: '✨ Stardust', price: 8 },
  { id: 'comet_sugar', name: '🍬 Comet Sugar', price: 8 },
  { id: 'space_bun', name: '🍔 Space Bun', price: 7 },
  { id: 'nebula_lettuce', name: '🥬 Nebula Lettuce', price: 7 }
];

export function createMoonTownScene(){ return () => new MoonTownScene(); }

class MoonTownScene {
  constructor(){
    this.panels = [];
    this.floatT = 0;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(NIGHT);
    this.scene.fog = new THREE.Fog(NIGHT, 34, 74);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 140);
    this.camDist = 16; this.camTarget = new THREE.Vector3(0, 1.6, 1);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildGround();
    this._buildStars();
    this._buildBubbleHomes();
    this._buildCastleGate();
    this._buildShip();
    this._buildCheeseShop();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 8);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.brie = this._makeCheeseVendor();  this.scene.add(this.brie.root);
    this.pirates = this._makePirates();    this.pirates.forEach(p => this.scene.add(p.root));

    this.hotspots = [
      { pos: this.brie.root.position.clone(), r: 2.4, label: '🧀 Brie Bella\'s Cheese Shop', action: () => this._cheeseShop() },
      { pos: new THREE.Vector3(0, 0, -12), r: 3.2, label: '🏰 enter the Moon Castle', action: () => this.manager.goTo('moonCastle', { from: 'moonTown' }) },
      { pos: new THREE.Vector3(7, 0, 4), r: 2.6, label: '🛰 ship console', action: () => this._shipConsole() },
      { pos: new THREE.Vector3(0, 0, 11), r: 2.6, label: '← fly back to Earth', action: () => this.manager.goTo('spaceport', { from: 'moonTown' }) }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x8890B0, 0.6));
    const earth = new THREE.DirectionalLight(0xBFD8FF, 0.7); earth.position.set(-8, 10, 12);
    earth.castShadow = true; earth.shadow.mapSize.set(2048, 2048);
    const s = 26; earth.shadow.camera.left = -s; earth.shadow.camera.right = s; earth.shadow.camera.top = s; earth.shadow.camera.bottom = -s;
    this.scene.add(earth);
  }

  _buildGround(){
    const g = new THREE.Mesh(new THREE.CircleGeometry(34, 56), new THREE.MeshLambertMaterial({ color: MOON }));
    g.rotation.x = -Math.PI/2; g.receiveShadow = true; this.scene.add(g);
    const plaza = new THREE.Mesh(new THREE.CircleGeometry(8, 40), new THREE.MeshLambertMaterial({ color: MOON_D }));
    plaza.rotation.x = -Math.PI/2; plaza.position.y = 0.01; this.scene.add(plaza);
    // scattered craters
    for (let i = 0; i < 16; i++){
      const a = i * 2.39, r = 10 + (i % 5) * 3.5;
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.8 + (i % 3) * 0.4, 0.18, 6, 16), new THREE.MeshLambertMaterial({ color: CRATER }));
      ring.rotation.x = -Math.PI/2; ring.position.set(Math.cos(a) * r, 0.03, Math.sin(a) * r); this.scene.add(ring);
    }
  }

  _buildStars(){
    const geo = new THREE.BufferGeometry(); const pts = [];
    for (let i = 0; i < 400; i++){
      const a = Math.random() * Math.PI * 2, el = Math.random() * Math.PI * 0.5, r = 60;
      pts.push(Math.cos(a) * Math.cos(el) * r, Math.sin(el) * r + 6, Math.sin(a) * Math.cos(el) * r);
    }
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    this.scene.add(new THREE.Points(geo, new THREE.PointsMaterial({ color: 0xFFFFFF, size: 0.4 })));
    // Earth hanging in the sky
    const earth = sphere(4, 0x3B7CC4, -16, 20, -28, this.scene, 24);
    earth.material = new THREE.MeshBasicMaterial({ color: 0x3B7CC4 });
    sphere(1.4, 0x5FAE6A, -14, 22, -26.5, this.scene, 16).material = new THREE.MeshBasicMaterial({ color: 0x5FAE6A });
  }

  _buildBubbleHomes(){
    const home = (x, z, col) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
      const dome = sphere(1.6, 0xBFE0FF, 0, 0.9, 0, g, 20); dome.scale.y = 0.9;
      dome.material = new THREE.MeshLambertMaterial({ color: 0xBFE0FF, transparent: true, opacity: 0.4 });
      cyl(1.65, 1.75, 0.4, MOON_D, 0, 0.2, 0, g, 20);        // base ring
      box(0.7, 1.0, 0.1, col, 0, 0.5, 1.5, g);               // little door
      sphere(0.5, col, -0.2, 0.9, 0, g, 12);                 // interior blob (a home)
      return g;
    };
    home(-8, 3, 0xE6537B); home(-9, -4, 0xF7C948); home(8, -3, 0x7FBF6A);
    home(-4, -8, 0x8FB8FF); home(5, 6, 0xE89AC7);
  }

  _buildCastleGate(){
    // silhouette of the castle at the north edge (real interior is moonCastle scene)
    const g = new THREE.Group(); g.position.set(0, 0, -15); this.scene.add(g);
    box(6, 4, 3, 0x8A7AA0, 0, 2, 0, g);
    cyl(1, 1.1, 5, 0x8A7AA0, -3, 2.5, 0, g, 10); cyl(1, 1.1, 5, 0x8A7AA0, 3, 2.5, 0, g, 10);
    cyl(0, 1.2, 1.6, 0xC0A8E0, -3, 5.6, 0, g, 10); cyl(0, 1.2, 1.6, 0xC0A8E0, 3, 5.6, 0, g, 10);
    box(1.6, 2.4, 0.3, 0x3A2E4A, 0, 1.2, 1.5, g);            // gate
    const sign = textSprite('🏰 Moon Castle', 26, '#EAE0FF', 4.2, 1.0); sign.position.set(0, 7.4, -15); this.scene.add(sign);
  }

  _buildShip(){
    const g = new THREE.Group(); g.position.set(7, 0, 3.5); this.scene.add(g); this.ship = g;
    cyl(0.8, 0.95, 3.6, 0xE8E4DC, 0, 1.8, 0, g, 20);
    cyl(0, 0.8, 1.3, 0xC94F4F, 0, 4.1, 0, g, 20);
    for (let i = 0; i < 3; i++){ const a = i/3*Math.PI*2; const f = box(0.15, 1.0, 0.9, 0xC94F4F, Math.cos(a)*0.9, 0.6, Math.sin(a)*0.9, g); f.rotation.y = -a; }
    box(1.4, 0.15, 1.4, MOON_D, 0, 0.05, 0, g);              // landing pad
    for (const [x,z] of [[-0.8,-0.8],[0.8,-0.8],[-0.8,0.8],[0.8,0.8]]) { const leg = cyl(0.06,0.06,1.2, METAL(), x, 0.6, z, g, 6); leg.rotation.x = z>0?0.3:-0.3; }
  }

  _buildCheeseShop(){
    const g = new THREE.Group(); g.position.set(-6, 0, 4); this.scene.add(g);
    const dome = sphere(2.0, 0xFFE9A8, 0, 1.0, 0, g, 20); dome.scale.y = 0.85;
    dome.material = new THREE.MeshLambertMaterial({ color: 0xFFE9A8, transparent: true, opacity: 0.55 });
    cyl(2.05, 2.15, 0.4, 0xC9A030, 0, 0.2, 0, g, 20);
    box(2.2, 0.2, 1.2, 0x8C6239, 0, 1.0, 2.0, g);           // counter
    sphere(0.35, 0xF4C430, -0.5, 1.3, 2.0, g, 12); sphere(0.3, 0xF4C430, 0.4, 1.25, 2.0, g, 12);
    const sign = textSprite('🧀 Lunar Dairy', 24, '#6B4A2F', 3.4, 0.9); sign.position.set(-6, 3.4, 4); this.scene.add(sign);
  }

  // ---- NPCs ----
  _makeCheeseVendor(){
    const build = (g) => {
      box(0.85, 1.0, 0.6, 0xFFF0B0, 0, 0.7, 0, g);            // brie wheel body (pale)
      sphere(.06, 0x2E2018, -0.16, 0.95, 0.32, g, 8); sphere(.06, 0x2E2018, 0.16, 0.95, 0.32, g, 8);
      box(0.3, 0.05, 0.05, 0x2E2018, 0, 0.78, 0.32, g);
      cyl(0.24, 0.28, 0.2, 0xE6537B, 0, 1.32, 0, g, 12);      // little hat
      stickLegs(g, 0.36);
    };
    return new Townsperson({ name: 'Brie Bella', line: 'Everything here is 100% dairy and 0% gravity!', pos: new THREE.Vector3(-6, 0, 6.2), facing: 0, wander: 0, speed: 0, bubbleY: 1.9, build });
  }

  _makePirates(){
    const dog = (col) => (g) => {
      box(0.7, 0.6, 0.9, col, 0, 0.7, 0, g);
      box(0.44, 0.44, 0.44, col, 0, 1.05, 0.45, g);
      box(0.2, 0.16, 0.22, 0x6E4A28, 0, 0.95, 0.72, g);
      sphere(0.05, 0x2E2018, -0.1, 1.12, 0.66, g, 8); sphere(0.05, 0x2E2018, 0.1, 1.12, 0.66, g, 8);
      const e1 = box(0.12, 0.3, 0.05, col, -0.2, 1.12, 0.35, g); e1.rotation.z = 0.4;
      const e2 = box(0.12, 0.3, 0.05, col, 0.2, 1.12, 0.35, g); e2.rotation.z = -0.4;
      box(0.5, 0.18, 0.5, 0x1F8F5A, 0, 0.9, 0.35, g);         // bandana
      for (const x of [-0.24,0.24]) for (const z of [-0.28,0.28]) cyl(0.09,0.09,0.5, 0x5A3A22, x, 0.25, z, g, 6);
    };
    const specs = [
      { name: 'Salty Wagtail', line: 'Bark twice if ye come in peace, once if ye brought treats!', col: 0xB07A44, pos: new THREE.Vector3(3, 0, 5) },
      { name: 'Captain Pawsilver', line: 'I once sailed the seven craters. True story, arrr.', col: 0x7A5A3A, pos: new THREE.Vector3(-2, 0, -3) }
    ];
    return specs.map(s => new Townsperson({ name: s.name, line: s.line, pos: s.pos, facing: Math.random()*6, wander: 2.2, speed: 0.7, bubbleY: 1.9, build: dog(s.col) }));
  }

  // ---- interactions ----
  _cheeseShop(){
    this.bread.stop(); this.bread.drawFace('grin');
    this._clearPanels();
    const { available, active } = questsForGiver('cheesemonger');
    const qRows = [];
    active.forEach(id => { const q = QUESTS[id]; qRows.push(isReady(id)
      ? `<div class="q-drow ready"><div><b>${q.title}</b><span>done — claim reward</span></div><button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`
      : `<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}</span></div><span class="q-dpend">in progress</span></div>`); });
    available.forEach(id => { const q = QUESTS[id]; qRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc} · 🍞 ${q.reward?.crumbs||0}</span></div><button class="cafe-btn accept" data-id="${id}">accept</button></div>`); });

    const fuelRow = getFlag('shipRefueled')
      ? `<div class="q-drow"><div><b>⛽ Moon Cheese Fuel</b><span>ship is fully fueled ✓</span></div><span class="q-dpend">done</span></div>`
      : `<div class="q-drow"><div><b>⛽ Moon Cheese Fuel</b><span>refuel the Starship · 🍞 ${FUEL_COST}</span></div><button class="cafe-btn buy-fuel">buy & refuel</button></div>`;
    const ingRows = MOON_INGREDIENTS.map(it => `<div class="q-drow"><div><b>${it.name}</b><span>🍞 ${it.price}</span></div><button class="cafe-btn buy-ing" data-id="${it.id}" data-price="${it.price}">buy</button></div>`).join('');

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Brie Bella's Lunar Dairy</h2>
        <div class="cafe-sub">moon cheese, fuel & fresh lunar goods</div>
        <div class="q-drows">${qRows.join('')}${fuelRow}${ingRows}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); HUD.refreshQuests(); this._cheeseShop(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => { if (turnIn(b.dataset.id)){ HUD.refreshCrumbs(); HUD.refreshQuests(); } this._cheeseShop(); });
    const fb = p.querySelector('.buy-fuel'); if (fb) fb.onclick = () => this._buyFuel();
    p.querySelectorAll('.buy-ing').forEach(b => b.onclick = () => this._buyIngredient(b.dataset.id, +b.dataset.price));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _buyFuel(){
    if (getFlag('shipRefueled')) return;
    if (getCrumbs() < FUEL_COST){ HUD.say(`You need ${FUEL_COST} crumbs for moon cheese fuel.`); return; }
    spendCrumbs(FUEL_COST); HUD.refreshCrumbs();
    setFlag('shipRefueled', true); flagObjective('moon-fuel', 'shipRefueled'); HUD.refreshQuests();
    this._cheeseShop();
  }

  _buyIngredient(id, price){
    if (getCrumbs() < price){ HUD.say(`Not enough crumbs (need ${price}).`); return; }
    spendCrumbs(price); addIngredient(id, 1); HUD.refreshCrumbs();
    HUD.say('Bought a fresh lunar ingredient — cook it at the stove or for the Moon King!');
    this._cheeseShop();
  }

  _shipConsole(){
    if (getFlag('navInstalled')){ HUD.say('The nav computer is online. The Starship Toaster is fully repaired!'); return; }
    if (!getFlag('gotNavChip')){ HUD.say('The nav slot is empty. The Moon King holds the navigation chip — win his favor in the castle.'); this.bread.drawFace('o'); return; }
    setFlag('navInstalled', true); flagObjective('space-final', 'navInstalled'); HUD.refreshQuests();
    HUD.say('You slot in the royal nav chip — the console lights up! Return to the Moon King to report in.');
    this.bread.drawFace('yum');
  }

  _panel(html){ const el = document.createElement('div'); el.className = 'cafe-panel'; el.innerHTML = html; document.body.appendChild(el); this.panels.push(el); return el; }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- lifecycle ----
  enter({ manager, from }){
    this.manager = manager; this.input = manager.input;
    this.input.useCamera(this.camera); this.input.polarRange = [0.3, 0.72];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('moon town'); HUD.refreshCrumbs(); HUD.refreshQuests();
    HUD.say(from === 'spaceport'
      ? 'You touch down on the moon! Low gravity, bubble homes, and cheese as far as the eye can see. Visit Brie Bella and the Moon Castle.'
      : 'Moon Town — bubble homes, cheese vendors, and the Moon Castle to the north.');
    HUD.setHint('click to walk · buy fuel & moon food at the dairy · enter the castle');
    HUD.hideHintSoon(8000);
  }
  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    if (checkNpcClick(ray, [this.brie, ...this.pirates], this.bread)) return;
    const dir = ray.ray.direction; if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y; if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(Math.max(-14, Math.min(14, p.x)), 0, Math.max(-11, Math.min(12, p.z)));
    HUD.hideHint(); this.bread.walkTo(gp);
  }

  update(dt){
    // low-gravity floaty bob on the character + pirates
    this.floatT += dt;
    this.bread.update(dt);
    this.bread.root.position.y = Math.abs(Math.sin(this.floatT * 2)) * 0.12;
    this.brie.update(dt, this.bread.root.position);
    this.pirates.forEach((p, i) => { p.update(dt, this.bread.root.position); p.root.position.y = Math.abs(Math.sin(this.floatT * 1.6 + i)) * 0.14; });

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){ this.camera.aspect = window.innerWidth / window.innerHeight; this.camera.updateProjectionMatrix(); }
}

// local metal helper (avoid importing extra palette tokens)
function METAL(){ return 0x9AA4B0; }
